import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toPng } from 'html-to-image';
import { Download, RefreshCw, Image, Share2, Plus, Trash2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';

// Utility to convert image URL to data URL with better error handling
const imageToDataUrl = async (url) => {
  if (!url) return null;
  
  // Already a data URL - return as-is
  if (url.startsWith('data:')) return url;
  
  // Ensure URL is absolute
  let absoluteUrl = url;
  if (url.startsWith('/')) {
    absoluteUrl = `${window.location.origin}${url}`;
  }
  
  try {
    // Try fetch with CORS
    const response = await fetch(absoluteUrl, { 
      mode: 'cors',
      cache: 'no-cache' // Avoid cache issues
    });
    
    if (!response.ok) {
      console.warn(`Failed to fetch image (${response.status}):`, absoluteUrl);
      return null;
    }
    
    const blob = await response.blob();
    
    // Verify it's actually an image
    if (!blob.type.startsWith('image/')) {
      console.warn('Response is not an image:', blob.type, absoluteUrl);
      return null;
    }
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result && reader.result.startsWith('data:')) {
          resolve(reader.result);
        } else {
          console.warn('FileReader result invalid for:', absoluteUrl);
          resolve(null);
        }
      };
      reader.onerror = () => {
        console.warn('FileReader error for:', absoluteUrl);
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('Failed to convert image to data URL:', absoluteUrl, err.message);
    
    // Fallback: Try loading via Image element (may work for some CORS scenarios)
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          resolve(dataUrl);
        } catch (canvasErr) {
          console.warn('Canvas export failed (CORS):', absoluteUrl);
          resolve(null);
        }
      };
      img.onerror = () => {
        console.warn('Image load failed:', absoluteUrl);
        resolve(null);
      };
      img.src = absoluteUrl;
    });
  }
};

const ASPECT_RATIOS = {
  instagram: { width: 1080, height: 1080, label: 'Instagram (1:1)' },
  facebook: { width: 1200, height: 630, label: 'Facebook/Twitter (1.91:1)' },
  story: { width: 1080, height: 1920, label: 'Story (9:16)' },
};

// Auto-scaling text component
const ScalingText = ({ children, maxWidth, initialSize = 50, minSize = 24, className = '' }) => {
  const textRef = useRef(null);
  const [fontSize, setFontSize] = useState(initialSize);

  useEffect(() => {
    if (!textRef.current) return;
    
    // Reset to initial size first
    setFontSize(initialSize);
    
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      if (!textRef.current) return;
      
      const element = textRef.current;
      let currentSize = initialSize;
      
      // Check if text overflows and reduce size until it fits
      while (element.scrollWidth > maxWidth && currentSize > minSize) {
        currentSize -= 2;
        element.style.fontSize = `${currentSize}px`;
      }
      
      setFontSize(currentSize);
    });
  }, [children, maxWidth, initialSize, minSize]);

  return (
    <span 
      ref={textRef}
      className={className}
      style={{ 
        fontSize: `${fontSize}px`,
        display: 'inline-block',
        whiteSpace: 'nowrap',
        maxWidth: maxWidth,
      }}
    >
      {children}
    </span>
  );
};

export const MatchGraphicGenerator = ({ games = [], leagues = [] }) => {
  const [selectedGameId, setSelectedGameId] = useState('');
  const [aspectRatio, setAspectRatio] = useState('instagram');
  const [generating, setGenerating] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('none');
  const [newLocationName, setNewLocationName] = useState('');
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [imageZoom, setImageZoom] = useState(100);
  const [imagePosition, setImagePosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [cachedImages, setCachedImages] = useState({}); // Cache for data URLs
  const [imagesReady, setImagesReady] = useState(false);
  const graphicRef = useRef(null);
  const imageContainerRef = useRef(null);

  // Load locations
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const res = await api.getLocations();
        setLocations(res.data);
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };
    loadLocations();
  }, []);

  // Get full game data when selection changes
  useEffect(() => {
    if (selectedGameId) {
      const game = games.find(g => g.id === selectedGameId);
      setSelectedGame(game);
    } else {
      setSelectedGame(null);
    }
  }, [selectedGameId, games]);

  const getLeagueLogo = useCallback((leagueId) => {
    const league = leagues.find(l => l.id === leagueId);
    return league?.logo;
  }, [leagues]);

  // Pre-load and cache images when game is selected
  useEffect(() => {
    const cacheImages = async () => {
      if (!selectedGame) {
        setCachedImages({});
        setImagesReady(false);
        return;
      }

      setImagesReady(false);
      const leagueLogo = getLeagueLogo(selectedGame.league_id);
      const urls = {
        homeTeamLogo: resolveMediaUrl(selectedGame.home_team_logo),
        awayTeamLogo: resolveMediaUrl(selectedGame.away_team_logo),
        leagueLogo: resolveMediaUrl(leagueLogo),
        backgroundImage: resolveMediaUrl(selectedGame.background_image_url),
      };

      console.log('Caching images:', urls); // Debug log

      const cached = {};
      for (const [key, url] of Object.entries(urls)) {
        if (url) {
          cached[key] = await imageToDataUrl(url);
        }
      }
      
      setCachedImages(cached);
      setImagesReady(true);
    };

    cacheImages();
  }, [selectedGame, getLeagueLogo]);

  const getSelectedLocation = () => {
    if (selectedLocationId === 'none') return null;
    return locations.find(l => l.id === selectedLocationId);
  };

  // Get the watch URL for QR code
  const getWatchUrl = (game) => {
    if (!game) return 'https://wpsl.tv';
    if (game.video_sources && game.video_sources.length > 0) {
      return game.video_sources[0].url || 'https://wpsl.tv';
    }
    return 'https://wpsl.tv';
  };

  // Calculate scaled font size based on text length and available width
  // Uses 95% of maxWidth to ensure text doesn't touch edges
  const getScaledFontSize = (text, maxWidth, maxSize, minSize) => {
    if (!text) return maxSize;
    
    // Use 95% of available width
    const targetWidth = maxWidth * 0.95;
    
    // More accurate character width estimation for Montserrat Bold/Black
    // Uppercase letters are wider than lowercase
    const avgCharWidth = 0.65; // Montserrat Black is wider
    const estimatedWidth = text.length * maxSize * avgCharWidth;
    
    if (estimatedWidth <= targetWidth) {
      return maxSize;
    }
    
    // Scale down proportionally
    const scaleFactor = targetWidth / estimatedWidth;
    const newSize = Math.max(Math.floor(maxSize * scaleFactor), minSize);
    
    return newSize;
  };

  const formatMatchDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatMatchTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleAddLocation = async () => {
    if (!newLocationName.trim()) return;
    try {
      const res = await api.createLocation({ name: newLocationName.trim() });
      setLocations([...locations, res.data]);
      setSelectedLocationId(res.data.id);
      setNewLocationName('');
      setShowAddLocation(false);
    } catch (error) {
      console.error('Error creating location:', error);
    }
  };

  const handleDeleteLocation = async (id) => {
    try {
      await api.deleteLocation(id);
      setLocations(locations.filter(l => l.id !== id));
      if (selectedLocationId === id) {
        setSelectedLocationId('none');
      }
    } catch (error) {
      console.error('Error deleting location:', error);
    }
  };

  // Handle image dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.image-drag-area')) {
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const rect = imageContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setImagePosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Reset image position
  const resetImagePosition = () => {
    setImageZoom(100);
    setImagePosition({ x: 50, y: 50 });
  };

  const handleDownload = async () => {
    if (!graphicRef.current || !selectedGame) return;
    
    // Wait for images to be ready
    if (!imagesReady) {
      alert('Please wait for images to load before downloading.');
      return;
    }
    
    setGenerating(true);
    try {
      const { width, height } = ASPECT_RATIOS[aspectRatio];
      
      // Temporarily remove the scale transform for accurate export
      const originalTransform = graphicRef.current.style.transform;
      graphicRef.current.style.transform = 'none';
      
      const dataUrl = await toPng(graphicRef.current, {
        width: width,
        height: height,
        pixelRatio: 2,
        backgroundColor: '#0a1628',
        style: {
          transform: 'none',
        },
        // Skip fonts that can't be loaded to avoid CORS issues
        skipFonts: true,
        // Filter out stylesheets that might cause CORS issues
        filter: (node) => {
          if (node.tagName === 'LINK' && node.getAttribute('rel') === 'stylesheet') {
            return false;
          }
          return true;
        },
        // Use cached images
        imagePlaceholder: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
      });
      
      // Restore the transform
      graphicRef.current.style.transform = originalTransform;
      
      const link = document.createElement('a');
      link.download = `${selectedGame.home_team_name}-vs-${selectedGame.away_team_name}-${aspectRatio}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error generating graphic:', error);
      alert('Error generating graphic. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const { width, height } = ASPECT_RATIOS[aspectRatio];
  const scale = Math.min(550 / width, 550 / height);
  const selectedLocation = getSelectedLocation();

  // Adjust content positioning based on aspect ratio
  const getContentStyle = () => {
    // All formats: align to top with 8% margin (shifted up)
    return { 
      paddingTop: '8%', 
      justifyContent: 'flex-start',
      alignItems: 'center'
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Share2 className="w-5 h-5 text-sky-400" />
        <h3 className="text-lg font-semibold text-white">Match Graphic Generator</h3>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <Label className="text-gray-300 mb-2 block">Select Match</Label>
          <Select value={selectedGameId} onValueChange={setSelectedGameId}>
            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
              <SelectValue placeholder="Choose a match..." />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
              {games.map(game => (
                <SelectItem key={game.id} value={game.id} className="text-white">
                  {game.home_team_name} vs {game.away_team_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-gray-300 mb-2 block">Aspect Ratio</Label>
          <Select value={aspectRatio} onValueChange={setAspectRatio}>
            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              {Object.entries(ASPECT_RATIOS).map(([key, { label }]) => (
                <SelectItem key={key} value={key} className="text-white">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-gray-300 mb-2 block">Location (Optional)</Label>
          <div className="flex gap-2">
            <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white flex-1">
                <SelectValue placeholder="No location" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="none" className="text-gray-400">No location</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id} className="text-white">
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              onClick={() => setShowAddLocation(!showAddLocation)}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-end">
          <Button
            onClick={handleDownload}
            disabled={!selectedGame || generating || !imagesReady}
            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold w-full"
          >
            {generating ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : !imagesReady && selectedGame ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading Images...</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Download Graphic</>
            )}
          </Button>
        </div>
      </div>

      {/* Add Location Form */}
      {showAddLocation && (
        <div className="flex gap-2 items-end p-4 bg-gray-800/50 rounded-lg border border-gray-700">
          <div className="flex-1">
            <Label className="text-gray-300 mb-2 block">New Location Name</Label>
            <Input
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value)}
              placeholder="e.g., Gaelic Park, Bronx"
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>
          <Button onClick={handleAddLocation} className="bg-sky-500 hover:bg-sky-400 text-white">
            Add Location
          </Button>
        </div>
      )}

      {/* Logo Status Warning */}
      {selectedGame && imagesReady && (
        <div className="space-y-2">
          {(!selectedGame.home_team_logo || !cachedImages.homeTeamLogo) && (
            <div className="flex items-center gap-2 p-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg">
              <span className="text-yellow-400 text-sm">
                ⚠️ <strong>{selectedGame.home_team_name}</strong> has no logo. Upload a logo in the Teams section for best results.
              </span>
            </div>
          )}
          {(!selectedGame.away_team_logo || !cachedImages.awayTeamLogo) && (
            <div className="flex items-center gap-2 p-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg">
              <span className="text-yellow-400 text-sm">
                ⚠️ <strong>{selectedGame.away_team_name}</strong> has no logo. Upload a logo in the Teams section for best results.
              </span>
            </div>
          )}
          {!selectedGame.background_image_url && (
            <div className="flex items-center gap-2 p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg">
              <span className="text-blue-400 text-sm">
                💡 No hero background image set for this match. Add one in the Games section for a more visual graphic.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Saved Locations List */}
      {locations.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {locations.map(loc => (
            <div key={loc.id} className="flex items-center gap-1 bg-gray-800 px-3 py-1 rounded-full text-sm">
              <MapPin className="w-3 h-3 text-gray-400" />
              <span className="text-gray-300">{loc.name}</span>
              <button
                onClick={() => handleDeleteLocation(loc.id)}
                className="ml-1 text-gray-500 hover:text-red-400"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Image Position Controls */}
      {selectedGame && selectedGame.background_image_url && (
        <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-white flex items-center gap-2">
              <Image className="w-4 h-4 text-sky-400" />
              Hero Image Controls
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetImagePosition}
              className="text-gray-400 hover:text-white text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Reset
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">Zoom: {imageZoom}%</Label>
              <input
                type="range"
                min="100"
                max="200"
                value={imageZoom}
                onChange={(e) => setImageZoom(Number(e.target.value))}
                className="w-full accent-sky-500"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">Position X: {imagePosition.x.toFixed(0)}%</Label>
              <input
                type="range"
                min="0"
                max="100"
                value={imagePosition.x}
                onChange={(e) => setImagePosition(prev => ({ ...prev, x: Number(e.target.value) }))}
                className="w-full accent-sky-500"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">Position Y: {imagePosition.y.toFixed(0)}%</Label>
              <input
                type="range"
                min="0"
                max="100"
                value={imagePosition.y}
                onChange={(e) => setImagePosition(prev => ({ ...prev, y: Number(e.target.value) }))}
                className="w-full accent-sky-500"
              />
            </div>
          </div>
          <p className="text-gray-500 text-xs">Tip: You can also drag the image in the preview to reposition it.</p>
        </div>
      )}

      {/* Preview */}
      <div className="flex justify-center">
        <div 
          ref={imageContainerRef}
          className={`relative bg-gray-900 rounded-lg overflow-hidden border border-gray-700 ${isDragging ? 'cursor-grabbing' : ''}`}
          style={{ width: width * scale, height: height * scale }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {selectedGame ? (
            <div
              ref={graphicRef}
              className="absolute inset-0 origin-top-left"
              style={{ 
                width: width,
                height: height,
                transform: `scale(${scale})`,
              }}
            >
              {/* Background Image with zoom and position */}
              {cachedImages.backgroundImage ? (
                <div className="absolute inset-0 overflow-hidden image-drag-area cursor-grab">
                  <img 
                    src={cachedImages.backgroundImage} 
                    alt="" 
                    className="absolute w-full h-full"
                    style={{
                      transform: `scale(${imageZoom / 100})`,
                      objectFit: 'cover',
                      objectPosition: `${imagePosition.x}% ${imagePosition.y}%`,
                      transformOrigin: `${imagePosition.x}% ${imagePosition.y}%`,
                    }}
                  />
                </div>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#1a2d4a] to-[#0d1a2d]" />
              )}
              
              {/* Overlay Gradients */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/40" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-transparent" />
              
              {/* Decorative diagonal line */}
              <div className="absolute inset-0 overflow-hidden">
                <div 
                  className="absolute top-0 bottom-0 left-1/2 w-[4px] bg-gradient-to-b from-transparent via-amber-500/50 to-transparent"
                  style={{ transform: 'translateX(-50%) skewX(-12deg)' }}
                />
              </div>

              {/* Content */}
              <div 
                className="absolute inset-0 flex flex-col items-center"
                style={getContentStyle()}
              >
                {/* Top - WPSL TV Logo */}
                <div className="text-center mb-6">
                  <img 
                    src="/wpsl-logo.svg" 
                    alt="WPSL TV" 
                    className="h-20 mx-auto"
                  />
                </div>

                {/* Match Info Section */}
                <div className="flex flex-col items-center w-full px-8">
                  {/* Team Logos Row - 3x larger */}
                  <div className="flex items-center justify-center gap-4 mb-0">
                    {/* Home Team Logo */}
                    <div className="w-80 h-80 flex items-center justify-center">
                      {cachedImages.homeTeamLogo ? (
                        <img 
                          src={cachedImages.homeTeamLogo} 
                          alt="" 
                          className="w-full h-full object-contain drop-shadow-2xl"
                        />
                      ) : (
                        <div className="w-full h-full bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
                          <span className="text-8xl font-bold text-white">{selectedGame.home_team_name?.charAt(0)}</span>
                        </div>
                      )}
                    </div>

                    {/* League Logo */}
                    <div className="w-28 h-28 flex items-center justify-center">
                      {cachedImages.leagueLogo ? (
                        <img 
                          src={cachedImages.leagueLogo} 
                          alt="" 
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <span className="text-2xl font-bold text-sky-300">VS</span>
                        </div>
                      )}
                    </div>

                    {/* Away Team Logo */}
                    <div className="w-80 h-80 flex items-center justify-center">
                      {cachedImages.awayTeamLogo ? (
                        <img 
                          src={cachedImages.awayTeamLogo} 
                          alt="" 
                          className="w-full h-full object-contain drop-shadow-2xl"
                        />
                      ) : (
                        <div className="w-full h-full bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
                          <span className="text-8xl font-bold text-white">{selectedGame.away_team_name?.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Match Title - Auto-scaling to 95% width, BOLD CAPS */}
                  <div 
                    className="flex justify-center"
                    style={{ 
                      marginTop: '60px',
                      width: '95%',
                      maxWidth: '95%',
                    }}
                  >
                    <h1 
                      className="leading-tight whitespace-nowrap uppercase text-center"
                      style={{ 
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 900,
                        color: 'white',
                        fontSize: getScaledFontSize(
                          `${selectedGame.home_team_name} VS ${selectedGame.away_team_name}`,
                          width * 0.95, // 95% of graphic width
                          48,
                          18
                        )
                      }}
                    >
                      {selectedGame.home_team_name} <span style={{ color: '#6b7280' }}>VS</span> {selectedGame.away_team_name}
                    </h1>
                  </div>

                  {/* League Name - 2x larger */}
                  <p className="text-sky-300 text-4xl font-semibold mb-6">{selectedGame.league_name}</p>

                  {/* Date & Time - 2x larger */}
                  <p className="text-gray-200 text-3xl font-medium mb-4">
                    {formatMatchDate(selectedGame.scheduled_time)}
                  </p>
                  <p className="text-gray-300 text-3xl">
                    {formatMatchTime(selectedGame.scheduled_time)}
                  </p>

                  {/* Location */}
                  {selectedLocation && (
                    <p className="text-amber-400 text-2xl mt-4 flex items-center justify-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                      <MapPin className="w-6 h-6 flex-shrink-0" />
                      <span>{selectedLocation.name}</span>
                    </p>
                  )}
                </div>

                {/* Bottom - CTA (positioned absolutely at bottom) */}
                <div className="absolute bottom-8 left-0 right-0 px-8">
                  <div className="flex items-center justify-center">
                    {/* CTA Button */}
                    <div 
                      className="inline-block bg-amber-500 text-black font-bold px-12 py-5 rounded-lg text-3xl"
                      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                    >
                      Watch Live
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a match to preview</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dimensions Info */}
      {selectedGame && (
        <p className="text-center text-gray-500 text-sm">
          Output: {width}×{height}px ({ASPECT_RATIOS[aspectRatio].label})
        </p>
      )}
    </div>
  );
};

export default MatchGraphicGenerator;
